/** Default and per-section SEO copy for SKK (slots, fishing, Shan Koe Mee, Bugyi, live casino, card games). */

/** Protocol + host (no trailing slash). Uses the live URL so one build works on SKK.biz, .cc, .tel, etc. */
export function getSiteOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

/**
 * Absolute canonical URL for a route pathname (must match router SEO / index.html bootstrap).
 * @param {string} [path] pathname e.g. /home (query/hash ignored)
 */
export function getCanonicalUrlForPath(path) {
  const pathname = ((path || '/').split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const origin = getSiteOrigin();
  if (!origin) return '';
  return pathname === '/' ? origin : `${origin}${pathname}`;
}

const BASE_DESC =
  'SKK offers online slots, fishing arcade games, Shan Koe Mee (Shan poker), Bugyi and classic card games, plus live casino with real dealers. Play securely, fast deposits and withdrawals, promotions for Myanmar players.';

const BASE_KEYWORDS =
  'SKK, online slots, slot games, fishing game, fishing arcade, Shan Koe Mee, Shan poker, ဘူဂျီ, Bugyi, Bugyee, online card games, live casino, live dealer, baccarat, roulette, Myanmar online casino, jackpot slots';

export const siteDefaults = {
  title: 'SKK | Online Slots, Fishing, Shan Koe Mee, Bugyi, Live Casino & Card Games',
  description: BASE_DESC,
  keywords: BASE_KEYWORDS,
};

export const pageMeta = {
  home: siteDefaults,

  slots: {
    title: 'Online Slot Games | SKK — Jackpots & Popular Slots',
    description:
      'Play hundreds of online slot games at SKK: classic reels, video slots, jackpots and bonus features. Fast play, fair results, mobile-friendly.',
    keywords: `${BASE_KEYWORDS}, video slots, jackpot slots, slot bonus`,
  },

  fishing: {
    title: 'Fishing Arcade Games Online | SKK',
    description:
      'Shoot-and-catch fishing arcade games at SKK. Colorful tables, multipliers and real-time action—popular in Myanmar and Southeast Asia.',
    keywords: `${BASE_KEYWORDS}, fishing shooter game, arcade fishing`,
  },

  liveCasino: {
    title: 'Live Casino & Live Dealer Games | SKK',
    description:
      'Live casino at SKK: real dealers, HD streams, baccarat, roulette and more. Feel like a real casino floor from your phone or desktop.',
    keywords: `${BASE_KEYWORDS}, live baccarat, live roulette, HD casino stream`,
  },

  shanKoeMee: {
    title: 'Shan Koe Mee Online Rooms | SKK',
    description:
      'Join Shan Koe Mee (Shan poker) tables at SKK. Play with friends or match online—authentic card rules and smooth gameplay.',
    keywords: `${BASE_KEYWORDS}, Shan Koe Mee room, Shan poker online`,
  },

  bugyee: {
    title: 'Bugyi (Bugyee) Card Game Online | SKK',
    description:
      'Play Bugyi / Bugyee card games online at SKK. Traditional Myanmar-style card play with reliable tables and quick rounds.',
    keywords: `${BASE_KEYWORDS}, Bugyi online, Bugyee card game`,
  },

  cardGames: {
    title: 'Online Card Games & Tables | SKK',
    description:
      'Card games and table games at SKK alongside slots and live casino. One account for multiple game types.',
    keywords: `${BASE_KEYWORDS}, table games, multiplayer cards`,
  },

  arcade: {
    title: 'Arcade & Casual Games | SKK',
    description: 'Arcade and casual games at SKK—quick fun between slots, fishing and live casino sessions.',
    keywords: `${BASE_KEYWORDS}, arcade games online`,
  },

  promotion: {
    title: 'Casino Promotions & Bonuses | SKK',
    description:
      'Latest SKK promotions: welcome offers, reload bonuses and cashback for slots, live casino and card games. Check terms on each offer.',
    keywords: `${BASE_KEYWORDS}, casino bonus, welcome bonus, cashback`,
  },

  login: {
    title: 'Member Login | SKK',
    description: 'Log in to your SKK account to play slots, fishing, Shan Koe Mee, Bugyi, live casino and card games.',
    keywords: `${BASE_KEYWORDS}, SKK login`,
  },

  register: {
    title: 'Register & Join | SKK',
    description:
      'Create a SKK account in minutes. Access slots, fishing games, Shan Koe Mee, Bugyi, live casino and secure banking.',
    keywords: `${BASE_KEYWORDS}, SKK register, sign up`,
  },

  contact: {
    title: 'Contact Us | SKK',
    description: 'Contact SKK support for account, deposits, withdrawals and game questions.',
    keywords: `${BASE_KEYWORDS}, SKK support, customer service`,
  },

  rule: {
    title: 'Rules & Terms | SKK',
    description: 'Rules, terms and responsible play information for SKK members.',
    keywords: `${BASE_KEYWORDS}, terms and conditions`,
  },

  deposit: {
    title: 'Deposit | SKK',
    description: 'Deposit funds securely to play slots, fishing, live casino and card games at SKK.',
    keywords: `${BASE_KEYWORDS}, deposit`,
    robots: 'noindex, nofollow',
  },

  withdraw: {
    title: 'Withdraw | SKK',
    description: 'Withdraw your winnings from SKK with supported payment methods.',
    keywords: `${BASE_KEYWORDS}, withdraw`,
    robots: 'noindex, nofollow',
  },

  banking: {
    title: 'Banking | SKK',
    description: 'Banking and payment options for SKK members.',
    keywords: `${BASE_KEYWORDS}, banking`,
    robots: 'noindex, nofollow',
  },

  history: {
    title: 'Transaction History | SKK',
    description: 'View your SKK transaction history.',
    robots: 'noindex, nofollow',
  },

  setting: {
    title: 'Account Settings | SKK',
    description: 'Manage your SKK account settings.',
    robots: 'noindex, nofollow',
  },

  betslip: {
    title: 'Betslip History | SKK',
    description: 'Your betslip history at SKK.',
    robots: 'noindex, nofollow',
  },

  liveScore: {
    title: 'Live Score | SKK',
    description: 'Live scores and sports updates at SKK.',
    keywords: BASE_KEYWORDS,
  },

  matchResult: {
    title: 'Football Match Results | SKK',
    description: 'View today and yesterday football match results at SKK.',
    keywords: `${BASE_KEYWORDS}, football results, match results`,
  },

  maungFb: {
    title: 'Maung FB | SKK',
    description: 'Maung FB at SKK.',
    keywords: BASE_KEYWORDS,
  },

  bodyFb: {
    title: 'Body FB | SKK',
    description: 'Body FB at SKK.',
    keywords: BASE_KEYWORDS,
  },

  videoLinks: {
    title: 'Video Links | SKK',
    description: 'Video links and guides at SKK.',
    keywords: BASE_KEYWORDS,
  },

  commission: {
    title: 'Commission | SKK',
    description: 'Commission program at SKK.',
    robots: 'noindex, nofollow',
  },

  level: {
    title: 'VIP Levels | SKK',
    description: 'VIP levels and rewards at SKK.',
    keywords: `${BASE_KEYWORDS}, VIP`,
  },

  guides: {
    title: 'App Install Guide | SKK',
    description: 'Install the SKK app on Android or iOS for slots, fishing, live casino and card games.',
    keywords: `${BASE_KEYWORDS}, SKK app, install guide`,
  },

  androidGuide: {
    title: 'Android App Install | SKK — Slots, Fishing & Live Casino',
    description:
      'Install SKK on Android: play online slots, fishing games, Shan Koe Mee, Bugyi and live casino on your phone.',
    keywords: `${BASE_KEYWORDS}, SKK Android APK`,
  },

  iosGuide: {
    title: 'iOS App Install | SKK — Slots, Fishing & Live Casino',
    description:
      'Install SKK on iPhone or iPad: slots, fishing arcade, card games and live dealer casino.',
    keywords: `${BASE_KEYWORDS}, SKK iOS app`,
  },

  dreamBooks: {
    title: 'Dream Books | SKK',
    description: 'Dream books feature at SKK.',
    keywords: BASE_KEYWORDS,
  },

  twoD: {
    title: '2D | SKK',
    description: '2D games at SKK.',
    keywords: BASE_KEYWORDS,
  },

  threeD: {
    title: '3D | SKK',
    description: '3D games at SKK.',
    keywords: BASE_KEYWORDS,
  },

  landing: {
    title: 'SKK — Slots, Fishing, Live Casino & Card Games',
    description: BASE_DESC,
    keywords: BASE_KEYWORDS,
  },

  iframe: {
    title: 'Game | SKK',
    robots: 'noindex, nofollow',
  },

  verify: {
    title: 'Verify Account | SKK',
    robots: 'noindex, nofollow',
  },

  dataTable: {
    title: 'Data | SKK',
    robots: 'noindex, nofollow',
  },
};
